/**
 * The long-press menu of an artifact card on phones (Open, Open full page, Share link) and the share itself.
 * The menu is a plain list so it can be tested; sharing goes through the native share sheet when the app runs
 * in Capacitor, the Web Share API on mobile web, and the clipboard otherwise.
 */
import { Capacitor } from '@capacitor/core'
import { isNativePlatform } from '@/utils/platform'
import { artifactRoute, artifactSurface, type ArtifactRef } from './artifacts'

export type ArtifactMenuAction = 'open' | 'page' | 'share'

export interface ArtifactMenuItem {
    id: ArtifactMenuAction
    label: string
    icon: string
}

/** The record's own page (a project, an invoice, a thread), not a section it lives in (Home, Pitching). */
export function recordRoute(a: ArtifactRef): string | null {
    const route = artifactRoute(a)
    if (!route) return null
    const own = a.type === 'draft' ? (a.data?.thread ?? a.data?.thread_id) : a.id
    return own && route.includes(encodeURIComponent(String(own))) ? route : null
}

/** What a long press on this artifact offers. Empty when there's nothing to do with it. */
export function artifactMenu(a: ArtifactRef): ArtifactMenuItem[] {
    const out: ArtifactMenuItem[] = []
    const surface = artifactSurface(a)
    const route = recordRoute(a)
    if (surface !== 'none') out.push({ id: 'open', label: 'Open', icon: 'fa-regular fa-arrow-up-right' })
    if (route) {
        out.push({ id: 'page', label: 'Open full page', icon: 'fa-regular fa-arrow-up-right-from-square' })
        out.push({ id: 'share', label: 'Share link', icon: 'fa-regular fa-arrow-up-from-bracket' })
    }
    return out
}

/** The app's public address for a path (the native app's own origin isn't shareable). */
export function shareUrl(path: string): string {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env
    const base = env?.VITE_APP_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://app.joinlighthouse.ai')
    try {
        return new URL(path, base).toString()
    } catch {
        return path
    }
}

/**
 * Share a link to the record. Resolves 'shared' (a share sheet took it, or the creator dismissed it), 'copied'
 * (put on the clipboard) or 'failed'.
 */
export async function shareArtifactLink(a: ArtifactRef): Promise<'shared' | 'copied' | 'failed'> {
    const path = recordRoute(a)
    if (!path) return 'failed'
    const url = shareUrl(path)
    try {
        if (isNativePlatform() && Capacitor.isPluginAvailable('Share')) {
            const { Share } = await import('@capacitor/share')
            await Share.share({ title: a.title, url })
            return 'shared'
        }
        if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
            await navigator.share({ title: a.title, url })
            return 'shared'
        }
    } catch (e) {
        // Dismissing the share sheet rejects; that's an answer, not a failure.
        if (e instanceof Error && /cancel|abort/i.test(`${e.name} ${e.message}`)) return 'shared'
    }
    try {
        await navigator.clipboard.writeText(url)
        return 'copied'
    } catch {
        return 'failed'
    }
}
